/**
 * Robotics-tab extractors and high-level mappers.
 *
 * Pure functions only: no RTK / no Neo4j driver imports.
 */

import { extractNode, extractNodeList, extractRawNode, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import { extractPlcScadaTags } from './plc'
import { extractPlantMeta } from './provenance'
import type {
  CypherReadResult,
  PlantEquipment,
  PlantPLCTag,
  PlantRobotArm,
  PlantRobotCondition,
  PlantRobotController,
  PlantRobotIOSignal,
  PlantRobotJob,
  PlantRobotNodeDetail,
  PlantRobotPlcTagMapping,
  PlantRobotRelationGroup,
  PlantRobotRelationSite,
  PlantRobotSupervisionRef,
  PlantRobotTreeNode,
  PlantRobotTreeNodeKind,
  PlantRoboticsTree,
  PlantVisionData,
  PlantVisionDataRef,
  PlantVisionProcess,
  PlantVisionProcessLink,
  PlantVisionSignalLink
} from '../types'

function displayName(node: Record<string, unknown>): string {
  const display = node['display_name']
  if (typeof display === 'string' && display.length > 0) return display
  const name = node['name']
  if (typeof name === 'string' && name.length > 0) return name
  return (typeof node['id'] === 'string' ? node['id'] : node['_id']) as string
}

function extractRobotTreeKind(value: unknown): PlantRobotTreeNodeKind | null {
  if (value === 'RobotController' || value === 'RobotArm' || value === 'RobotJob') return value
  return null
}

function sortNodes<T extends { name?: string; id?: string }>(nodes: T[]): T[] {
  return nodes.sort(compareByNameThenId)
}

function dedupeById<T extends { id: string }>(nodes: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const node of nodes) {
    if (seen.has(node.id)) continue
    seen.add(node.id)
    out.push(node)
  }
  return out
}

/**
 * Detect cycles in the controller SUPERVISES parent graph and demote
 * cycle members to roots so `buildTreeByParentId` does not drop them.
 */
export function demoteSupervisesCycles(nodes: PlantRobotTreeNode[]): PlantRobotTreeNode[] {
  const parentOf = new Map<string, string | null>()
  for (const node of nodes) {
    if (node.kind !== 'RobotController') continue
    parentOf.set(node.id, node.parentId)
  }
  if (parentOf.size === 0) return nodes

  const color = new Map<string, 0 | 1 | 2>()
  const inCycle = new Set<string>()

  const dfs = (id: string, path: string[]): void => {
    color.set(id, 1)
    path.push(id)
    const parent = parentOf.get(id)
    if (parent && parentOf.has(parent)) {
      const state = color.get(parent) ?? 0
      if (state === 1) {
        const idx = path.indexOf(parent)
        for (let i = Math.max(0, idx); i < path.length; i += 1) {
          inCycle.add(path[i]!)
        }
      } else if (state === 0) {
        dfs(parent, path)
      }
    }
    path.pop()
    color.set(id, 2)
  }

  for (const id of parentOf.keys()) {
    if ((color.get(id) ?? 0) === 0) dfs(id, [])
  }
  if (inCycle.size === 0) return nodes

  return nodes.map((node) =>
    node.kind === 'RobotController' && inCycle.has(node.id) ? { ...node, parentId: null } : node
  )
}

function compareSites<T extends { name?: string; id?: string }>(
  a: PlantRobotRelationSite<T>,
  b: PlantRobotRelationSite<T>
): number {
  const lineA = typeof a.line === 'number' ? a.line : Number.POSITIVE_INFINITY
  const lineB = typeof b.line === 'number' ? b.line : Number.POSITIVE_INFINITY
  if (lineA !== lineB) return lineA - lineB
  return compareByNameThenId(a.node, b.node)
}

function siteDedupeKey(site: PlantRobotRelationSite<{ id: string }>): string {
  return [site.node.id, site.line ?? '', site.opcode ?? '', site.instruction ?? ''].join('\0')
}

/**
 * The graph write path stores list properties as JSON strings (`"[3,7]"`),
 * so accept either a real array or a JSON-encoded one.
 */
function decodeList(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string' || !value.trimStart().startsWith('[')) return null
  try {
    const decoded = JSON.parse(value) as unknown
    return Array.isArray(decoded) ? decoded : null
  } catch {
    return null
  }
}

function toNumberList(value: unknown): Array<number | undefined> | null {
  const list = decodeList(value)
  if (!list) return null
  return list.map((v) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined))
}

function toStringList(value: unknown): Array<string | undefined> | null {
  const list = decodeList(value)
  if (!list) return null
  return list.map((v) => (typeof v === 'string' && v.length > 0 ? v : undefined))
}

/**
 * Expand one relationship row into per-site entries. Rows carry the edge's
 * instruction sites as parallel lists (`instruction_indexes` / `opcodes` /
 * `instructions`, index i = one site); legacy rows with scalar `line` /
 * `opcode` / `instruction` yield a single site. A row with no site info at
 * all still yields one bare entry so the peer stays visible.
 */
function expandRelationSiteRow<T extends { id: string; _id: string; name: string }>(
  obj: Record<string, unknown>,
  node: T
): PlantRobotRelationSite<T>[] {
  const indexes = toNumberList(obj['instruction_indexes'])
  const opcodes = toStringList(obj['opcodes'])
  const instructions = toStringList(obj['instructions'])
  const count = Math.max(indexes?.length ?? 0, opcodes?.length ?? 0, instructions?.length ?? 0)

  if (count === 0) {
    const site: PlantRobotRelationSite<T> = { node }
    if (typeof obj['instruction'] === 'string' && obj['instruction'].length > 0) {
      site.instruction = obj['instruction']
    }
    if (typeof obj['line'] === 'number' && Number.isFinite(obj['line'])) {
      site.line = obj['line']
    }
    if (typeof obj['opcode'] === 'string' && obj['opcode'].length > 0) {
      site.opcode = obj['opcode']
    }
    return [site]
  }

  const sites: PlantRobotRelationSite<T>[] = []
  for (let i = 0; i < count; i += 1) {
    const site: PlantRobotRelationSite<T> = { node }
    const line = indexes?.[i]
    if (line !== undefined) site.line = line
    const opcode = opcodes?.[i]
    if (opcode !== undefined) site.opcode = opcode
    const instruction = instructions?.[i]
    if (instruction !== undefined) site.instruction = instruction
    sites.push(site)
  }
  return sites
}

export function extractRobotRelationSites<T extends { id: string; _id: string; name: string }>(
  value: unknown
): PlantRobotRelationSite<T>[] {
  if (!Array.isArray(value)) return []
  const out: PlantRobotRelationSite<T>[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const node = extractNode<T>(obj['node'])
    if (!node) continue
    for (const site of expandRelationSiteRow(obj, node)) {
      const key = siteDedupeKey(site)
      if (seen.has(key)) continue
      seen.add(key)
      out.push(site)
    }
  }
  return out.sort(compareSites)
}

/**
 * Collapse flat instruction sites into one row per peer, preserving every
 * distinct site under that peer (sorted by line).
 */
export function groupRobotRelationSites<T extends { id: string; name?: string }>(
  sites: PlantRobotRelationSite<T>[]
): PlantRobotRelationGroup<T>[] {
  const byId = new Map<string, PlantRobotRelationGroup<T>>()
  const order: string[] = []
  const seenSites = new Map<string, Set<string>>()
  for (const site of sites) {
    let group = byId.get(site.node.id)
    if (!group) {
      group = { node: site.node, sites: [] }
      byId.set(site.node.id, group)
      order.push(site.node.id)
      seenSites.set(site.node.id, new Set())
    }
    const entry: { instruction?: string; line?: number; opcode?: string } = {}
    if (site.instruction) entry.instruction = site.instruction
    if (typeof site.line === 'number') entry.line = site.line
    if (site.opcode) entry.opcode = site.opcode
    if (!(entry.instruction || entry.line !== undefined || entry.opcode)) continue
    const siteKey = `${entry.line ?? ''}\0${entry.opcode ?? ''}\0${entry.instruction ?? ''}`
    const seen = seenSites.get(site.node.id)!
    if (seen.has(siteKey)) continue
    seen.add(siteKey)
    group.sites.push(entry)
  }
  return order.map((id) => byId.get(id)!).sort((a, b) => compareByNameThenId(a.node, b.node))
}

export function extractRobotSupervisionRefs(value: unknown): PlantRobotSupervisionRef[] {
  if (!Array.isArray(value)) return []
  const out: PlantRobotSupervisionRef[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const controller = extractNode<PlantRobotController>(obj['controller'])
    if (!controller) continue
    const ref: PlantRobotSupervisionRef = { controller }
    if (typeof obj['role'] === 'string' && obj['role'].length > 0) ref.role = obj['role']
    if (typeof obj['networkPath'] === 'string' && obj['networkPath'].length > 0) {
      ref.networkPath = obj['networkPath']
    }
    out.push(ref)
  }
  return out.sort((a, b) => compareByNameThenId(a.controller, b.controller))
}

export function extractRobotPlcTagMappings(value: unknown): PlantRobotPlcTagMapping[] {
  if (!Array.isArray(value)) return []
  const out: PlantRobotPlcTagMapping[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const tag = extractNode<PlantPLCTag>(obj['tag'])
    if (!tag) continue
    const mapping: PlantRobotPlcTagMapping = { tag }
    if (typeof obj['method'] === 'string' && obj['method'].length > 0)
      mapping.method = obj['method']
    if (typeof obj['confidence'] === 'number' && Number.isFinite(obj['confidence'])) {
      mapping.confidence = obj['confidence']
    }
    if (typeof obj['register'] === 'string' && obj['register'].length > 0) {
      mapping.register = obj['register']
    }
    if (typeof obj['direction'] === 'string' && obj['direction'].length > 0) {
      mapping.direction = obj['direction']
    }
    out.push(mapping)
  }
  return out.sort((a, b) => compareByNameThenId(a.tag, b.tag))
}

/**
 * Project `robotSignalPlcRows` (`{ signalId, tag }[]`) into a per-signal
 * lookup for controller / arm / job signal tables.
 */
export function extractRobotPlcTagsBySignalId(value: unknown): Record<string, PlantPLCTag[]> {
  const out: Record<string, PlantPLCTag[]> = {}
  if (!Array.isArray(value)) return out
  const seenBySignal: Record<string, Set<string>> = {}
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const signalId = typeof obj['signalId'] === 'string' ? obj['signalId'] : null
    if (!signalId) continue
    const tag = extractNode<PlantPLCTag>(obj['tag'])
    if (!tag) continue
    const seen = seenBySignal[signalId] ?? new Set<string>()
    if (seen.has(tag.id)) continue
    seen.add(tag.id)
    seenBySignal[signalId] = seen
    const list = out[signalId] ?? []
    list.push(tag)
    out[signalId] = list
  }
  for (const signalId of Object.keys(out)) {
    out[signalId]!.sort(compareByNameThenId)
  }
  return out
}

function stringList(value: unknown): string[] {
  return (decodeList(value) ?? []).filter((v): v is string => typeof v === 'string' && v.length > 0)
}

function numberList(value: unknown): number[] {
  return (decodeList(value) ?? []).filter(
    (v): v is number => typeof v === 'number' && Number.isFinite(v)
  )
}

const VISION_STRING_LIST_PROPS = ['applications', 'result_kinds', 'operations', 'tools'] as const
const VISION_NUMBER_LIST_PROPS = ['vision_registers', 'camera_views'] as const

/** Decode the JSON-string list properties the graph stores on vision nodes. */
function withVisionLists<T extends PlantVisionProcess | PlantVisionData>(node: T): T {
  const raw = node as unknown as Record<string, unknown>
  const out: Record<string, unknown> = { ...raw }
  for (const key of VISION_STRING_LIST_PROPS) {
    if (raw[key] !== undefined) out[key] = stringList(raw[key])
  }
  for (const key of VISION_NUMBER_LIST_PROPS) {
    if (raw[key] !== undefined) out[key] = numberList(raw[key])
  }
  return out as unknown as T
}

function extractVisionNode<T extends PlantVisionProcess | PlantVisionData>(
  value: unknown
): T | null {
  const node = extractNode<T>(value)
  return node ? withVisionLists(node) : null
}

function extractVisionNodeList<T extends PlantVisionProcess | PlantVisionData>(
  value: unknown
): T[] {
  return extractNodeList<T>(value).map(withVisionLists)
}

function edgeProps(obj: Record<string, unknown>): Record<string, unknown> {
  const props = obj['props']
  return props && typeof props === 'object' ? (props as Record<string, unknown>) : {}
}

/**
 * Project `visionOutputRows` / `visionTriggerRows` (`{ signal, props,
 * plcTags, scadaRows }`) into signal links, deduping PLC and SCADA tags.
 */
export function extractVisionSignalLinks(value: unknown): PlantVisionSignalLink[] {
  if (!Array.isArray(value)) return []
  const out: PlantVisionSignalLink[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const signal = extractNode<PlantRobotIOSignal>(obj['signal'])
    if (!signal) continue
    const props = edgeProps(obj)
    const scadaSeen = new Set<string>()
    const scadaTags = extractPlcScadaTags(obj['scadaRows']).filter((tag) => {
      if (scadaSeen.has(tag.id)) return false
      scadaSeen.add(tag.id)
      return true
    })
    out.push({
      signal,
      resultKinds: stringList(props['result_kinds']),
      via: stringList(props['via']),
      jobs: stringList(props['jobs']),
      dataFields: stringList(props['vision_data_fields']),
      ...(typeof props['method'] === 'string' ? { method: props['method'] } : {}),
      plcTags: sortNodes(dedupeById(extractNodeList<PlantPLCTag>(obj['plcTags']))),
      scadaTags: scadaTags.sort(compareByNameThenId)
    })
  }
  return out.sort((a, b) => compareByNameThenId(a.signal, b.signal))
}

/** Project `signalVisionOutputs` / `signalVisionTriggers` (`{ process, props }`). */
export function extractVisionProcessLinks(value: unknown): PlantVisionProcessLink[] {
  if (!Array.isArray(value)) return []
  const out: PlantVisionProcessLink[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const process = extractVisionNode<PlantVisionProcess>(obj['process'])
    if (!process) continue
    const props = edgeProps(obj)
    out.push({
      process,
      resultKinds: stringList(props['result_kinds']),
      via: stringList(props['via']),
      jobs: stringList(props['jobs']),
      dataFields: stringList(props['vision_data_fields'])
    })
  }
  return out.sort((a, b) => compareByNameThenId(a.process, b.process))
}

/** Project `USES_VISION_DATA` peer rows (`{ node, props }`). */
export function extractVisionDataRefs<T extends PlantVisionProcess | PlantVisionData>(
  value: unknown
): PlantVisionDataRef<T>[] {
  if (!Array.isArray(value)) return []
  const out: PlantVisionDataRef<T>[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const node = extractVisionNode<T>(obj['node'])
    if (!node) continue
    const props = edgeProps(obj)
    out.push({
      node,
      ...(typeof props['method'] === 'string' ? { method: props['method'] } : {}),
      ...(typeof props['confidence'] === 'number' ? { confidence: props['confidence'] } : {}),
      needsReview: props['needs_review'] === true
    })
  }
  return out.sort((a, b) => compareByNameThenId(a.node, b.node))
}

function attachPlcTags(
  signal: PlantRobotIOSignal,
  plcTagsBySignalId: Record<string, PlantPLCTag[]>
): PlantRobotIOSignal {
  const plcTags = plcTagsBySignalId[signal.id]
  if (!plcTags || plcTags.length === 0) return signal
  return { ...signal, plcTags }
}

function attachPlcTagsToSites(
  sites: PlantRobotRelationSite<PlantRobotIOSignal>[],
  plcTagsBySignalId: Record<string, PlantPLCTag[]>
): PlantRobotRelationSite<PlantRobotIOSignal>[] {
  return sites.map((site) => {
    const node = attachPlcTags(site.node, plcTagsBySignalId)
    return node === site.node ? site : { ...site, node }
  })
}

export function mapPlantRoboticsTree(raw: CypherReadResult): PlantRoboticsTree {
  const nodes: PlantRobotTreeNode[] = []
  let totalControllerCount = 0
  let totalArmCount = 0
  let totalJobCount = 0
  for (const row of raw.results) {
    totalControllerCount = toIntCount(row['totalControllerCount'])
    totalArmCount = toIntCount(row['totalArmCount'])
    totalJobCount = toIntCount(row['totalJobCount'])
    const node = extractRawNode(row['node'])
    if (!node) continue
    const kind = extractRobotTreeKind(row['kind'])
    if (!kind) continue
    const id = typeof node['id'] === 'string' ? node['id'] : (node['_id'] as string)
    nodes.push({
      id,
      parentId: typeof row['parentId'] === 'string' ? row['parentId'] : null,
      kind,
      name: displayName(node),
      ...extractPlantMeta(node),
      armCount: toIntCount(row['armCount']),
      jobCount: toIntCount(row['jobCount']),
      signalCount: toIntCount(row['signalCount']),
      conditionCount: toIntCount(row['conditionCount']),
      needsReview: node['needs_review'] === true
    })
  }
  return {
    nodes: demoteSupervisesCycles(nodes),
    totalControllerCount,
    totalArmCount,
    totalJobCount
  }
}

export function mapPlantRobotNodeDetail(
  raw: CypherReadResult,
  nodeId: string
): PlantRobotNodeDetail {
  const row = raw.results[0]
  if (!row) {
    throw new Error(`Robotics node ${nodeId} not found`)
  }
  const labels = Array.isArray(row['labelList']) ? (row['labelList'] as unknown[]) : []
  const strayLabels = Array.isArray(row['strayLabels'])
    ? row['strayLabels'].filter((label): label is string => typeof label === 'string')
    : []
  const isStrayFor = (kind: string): boolean => strayLabels.includes(kind)
  const plcTagsBySignalId = extractRobotPlcTagsBySignalId(row['robotSignalPlcRows'])

  if (labels.includes('RobotController')) {
    const controller = extractNode<PlantRobotController>(row['n'])
    if (!controller) throw new Error(`RobotController ${nodeId} could not be projected`)
    return {
      kind: 'RobotController',
      isStray: isStrayFor('RobotController'),
      controller,
      arms: sortNodes(extractNodeList<PlantRobotArm>(row['controllerArms'])),
      jobs: sortNodes(extractNodeList<PlantRobotJob>(row['controllerJobs'])),
      signals: sortNodes(
        dedupeById(extractNodeList<PlantRobotIOSignal>(row['controllerSignals'])).map((s) =>
          attachPlcTags(s, plcTagsBySignalId)
        )
      ),
      conditions: sortNodes(
        dedupeById(extractNodeList<PlantRobotCondition>(row['controllerConditions']))
      ),
      controlledEquipment: sortNodes(extractNodeList<PlantEquipment>(row['controlledEquipment'])),
      visionProcesses: sortNodes(
        dedupeById(extractVisionNodeList<PlantVisionProcess>(row['controllerVisionProcesses']))
      ),
      visionData: sortNodes(
        dedupeById(extractVisionNodeList<PlantVisionData>(row['controllerVisionData']))
      ),
      supervises: extractRobotSupervisionRefs(row['supervisesRows']),
      supervisedBy: extractRobotSupervisionRefs(row['supervisedByRows'])
    }
  }

  if (labels.includes('RobotArm')) {
    const arm = extractNode<PlantRobotArm>(row['n'])
    if (!arm) throw new Error(`RobotArm ${nodeId} could not be projected`)
    return {
      kind: 'RobotArm',
      isStray: isStrayFor('RobotArm'),
      arm,
      controller: extractNode<PlantRobotController>(row['armController']),
      jobs: sortNodes(extractNodeList<PlantRobotJob>(row['armJobs'])),
      signals: sortNodes(
        extractNodeList<PlantRobotIOSignal>(row['armSignals']).map((s) =>
          attachPlcTags(s, plcTagsBySignalId)
        )
      ),
      conditions: sortNodes(extractNodeList<PlantRobotCondition>(row['armConditions']))
    }
  }

  if (labels.includes('RobotJob')) {
    const job = extractNode<PlantRobotJob>(row['n'])
    if (!job) throw new Error(`RobotJob ${nodeId} could not be projected`)
    return {
      kind: 'RobotJob',
      isStray: isStrayFor('RobotJob'),
      job,
      arm: extractNode<PlantRobotArm>(row['jobArm']),
      controller: extractNode<PlantRobotController>(row['jobController']),
      calls: extractRobotRelationSites<PlantRobotJob>(row['jobCalls']),
      calledBy: extractRobotRelationSites<PlantRobotJob>(row['jobCalledBy']),
      readSignals: attachPlcTagsToSites(
        extractRobotRelationSites<PlantRobotIOSignal>(row['jobReadSignals']),
        plcTagsBySignalId
      ),
      writtenSignals: attachPlcTagsToSites(
        extractRobotRelationSites<PlantRobotIOSignal>(row['jobWrittenSignals']),
        plcTagsBySignalId
      ),
      conditions: extractRobotRelationSites<PlantRobotCondition>(row['jobConditions']),
      visionProcesses: extractRobotRelationSites<PlantVisionProcess>(row['jobVisionProcesses']),
      visionData: extractRobotRelationSites<PlantVisionData>(row['jobVisionData'])
    }
  }

  if (labels.includes('RobotIOSignal')) {
    const signal = extractNode<PlantRobotIOSignal>(row['n'])
    if (!signal) throw new Error(`RobotIOSignal ${nodeId} could not be projected`)
    return {
      kind: 'RobotIOSignal',
      isStray: isStrayFor('RobotIOSignal'),
      signal,
      controller: extractNode<PlantRobotController>(row['signalController']),
      readBy: extractRobotRelationSites<PlantRobotJob>(row['signalReadBy']),
      writtenBy: extractRobotRelationSites<PlantRobotJob>(row['signalWrittenBy']),
      scadaTags: extractPlcScadaTags(row['signalScadaRows']),
      plcMappings: extractRobotPlcTagMappings(row['signalPlcMappings']),
      visionOutputs: extractVisionProcessLinks(row['signalVisionOutputs']),
      visionTriggers: extractVisionProcessLinks(row['signalVisionTriggers'])
    }
  }

  if (labels.includes('RobotCondition')) {
    const condition = extractNode<PlantRobotCondition>(row['n'])
    if (!condition) throw new Error(`RobotCondition ${nodeId} could not be projected`)
    return {
      kind: 'RobotCondition',
      isStray: isStrayFor('RobotCondition'),
      condition,
      controller: extractNode<PlantRobotController>(row['conditionController']),
      usedBy: extractRobotRelationSites<PlantRobotJob>(row['conditionUsedBy'])
    }
  }

  if (labels.includes('VisionProcess')) {
    const process = extractVisionNode<PlantVisionProcess>(row['n'])
    if (!process) throw new Error(`VisionProcess ${nodeId} could not be projected`)
    return {
      kind: 'VisionProcess',
      isStray: isStrayFor('VisionProcess'),
      process,
      controller: extractNode<PlantRobotController>(row['visionController']),
      usedBy: extractRobotRelationSites<PlantRobotJob>(row['visionUsedBy']),
      outputs: extractVisionSignalLinks(row['visionOutputRows']),
      triggers: extractVisionSignalLinks(row['visionTriggerRows']),
      visionData: extractVisionDataRefs<PlantVisionData>(row['visionDataRefs'])
    }
  }

  if (labels.includes('VisionData')) {
    const data = extractVisionNode<PlantVisionData>(row['n'])
    if (!data) throw new Error(`VisionData ${nodeId} could not be projected`)
    return {
      kind: 'VisionData',
      isStray: isStrayFor('VisionData'),
      data,
      controller: extractNode<PlantRobotController>(row['visionController']),
      usedBy: extractRobotRelationSites<PlantRobotJob>(row['visionUsedBy']),
      processes: extractVisionDataRefs<PlantVisionProcess>(row['visionDataUsedByProcesses'])
    }
  }

  throw new Error(`Robotics node ${nodeId} has unsupported labels ${labels.join(',')}`)
}
